/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  PolymorphicCommonProps,
  TooltipObjectProps,
} from '../_common/types';
import { TextLink } from '../text-link';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { COLLAPSE_BUTTON_TEXT, ELLIPSIS_CHAR } from './constants';
import {
  getTextContent,
  truncateReactNodeByCharacters,
} from './text-overflow-utils';
import { useTruncateWithButton } from './use-truncate-with-button';

type TextOverflowProps = {
  /** The content to be truncated. Can be text, Typography components, or complex JSX. */
  children: React.ReactNode;

  /**
   * Controls the expand/collapse UI.
   * - string: show expand/collapse button with custom text (e.g., "View more")
   * - undefined: content is not expandable, only truncated with ellipsis
   * @default undefined
   */
  expandButton?: 'view-more' | 'ellipsis';

  /**
   * Maximum number of characters to display. Takes precedence over line-based truncation when set.
   */
  maxCharacters?: number;

  /**
   * Number of lines to show before truncation.
   * Uses CSS line-clamp for non-expandable content.
   * Uses DOM measurement for expandable content.
   * **Note**: Ignored if `maxCharacters` is explicitly set to a custom value.
   */
  lines?: number;

  /**
   * Whether to show tooltip with full text on hover when content is truncated.
   * @default true
   */
  shouldShowTooltip?: boolean;

  /** Props to pass to tooltip component */
  tooltipProps?: TooltipObjectProps;

  /** Callback function called when content is expanded or collapsed */
  onExpandedToggle?: (isExpanded: boolean) => void;

  /**
   * The typography variant to use for the text.
   * @default 'body-medium'
   */
  typographyVariant?: React.ComponentProps<typeof Typography>['variant'];
};

const expandButtonTexts = {
  ellipsis: ELLIPSIS_CHAR,
  'view-more': 'View more',
};

export const TextOverflow = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  htmlAttributes,
  lines,
  tooltipProps,
  onExpandedToggle,
  expandButton,
  maxCharacters,
  shouldShowTooltip = true,
  typographyVariant = 'body-medium',
  ref,
  ...restProps
}: PolymorphicCommonProps<T, TextOverflowProps>) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const textElementRef = useRef<HTMLSpanElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // Use truncate with button hook when expandButton is provided
  const shouldUseTruncateWithButton = expandButton !== undefined;
  const truncateWithButtonResult = useTruncateWithButton({
    button: expandButtonTexts[expandButton!] || 'View more',
    buttonType: expandButton,
    closeTooltip: () => setIsTooltipOpen(false),
    content: children,
    // Disable expensive calculations when no expand button is needed
    isDisabled: !shouldUseTruncateWithButton,
    lineClamp: lines ?? 1,
    maxCharacters,
    onExpandedToggle,
  });

  // Handle character-based truncation when no expand button is used
  const characterTruncatedContent = useMemo(() => {
    if (maxCharacters !== undefined && expandButton === undefined) {
      const result = truncateReactNodeByCharacters(children, maxCharacters);
      return result.shouldTruncation ? result.truncatedContent : null;
    }
    return null;
  }, [maxCharacters, expandButton, children]);

  // Memoize text content extraction to avoid repeated traversals
  const textContent = useMemo(() => getTextContent(children), [children]);

  // Memoize tooltip truncation detection logic
  const checkTruncationForTooltip = useCallback(() => {
    if (shouldUseTruncateWithButton) {
      return truncateWithButtonResult.isTruncated;
    }

    // Character based truncation
    if (maxCharacters !== undefined) {
      return characterTruncatedContent !== null;
    }

    const element = textElementRef.current;
    if (element === null) {
      return false;
    }

    // Regular multiline truncation
    if (lines !== undefined && lines > 1) {
      return element.scrollHeight > element.clientHeight;
    }

    // Regular single line truncation
    return element.scrollWidth > element.clientWidth;
  }, [
    shouldUseTruncateWithButton,
    truncateWithButtonResult.isTruncated,
    maxCharacters,
    characterTruncatedContent,
    lines,
  ]);

  const renderContent = () => {
    const isMultiline = typeof lines === 'number' && lines > 1;

    const textStyle = {
      '--ndl-line-clamp': isMultiline ? lines.toString() : undefined,
    } as React.CSSProperties;

    const textClasses = classNames({
      'ndl-text-overflow-multi-line': isMultiline,
      'ndl-text-overflow-single-line':
        !isMultiline && maxCharacters === undefined,
    });

    if (shouldUseTruncateWithButton) {
      return (
        <div ref={truncateWithButtonResult.containerRef}>
          <div
            className={truncateWithButtonResult.isExpanded ? '' : textClasses}
            style={textStyle}
          >
            {truncateWithButtonResult.contentToRender}
            {truncateWithButtonResult.isTruncated && (
              <TextLink
                as="button"
                className={classNames('ndl-text-overflow-toggle', {
                  'ndl-text-overflow-toggle-margin-left':
                    expandButton !== 'ellipsis' ||
                    truncateWithButtonResult.isExpanded,
                })}
                htmlAttributes={{
                  onClick: truncateWithButtonResult.toggleExpand,
                }}
              >
                {truncateWithButtonResult.isExpanded
                  ? COLLAPSE_BUTTON_TEXT
                  : expandButtonTexts[expandButton!]}
              </TextLink>
            )}
          </div>
          <div
            ref={truncateWithButtonResult.measureRef}
            style={{
              position: 'absolute',
              top: '-9999px',
              visibility: 'hidden',
            }}
          />
        </div>
      );
    }

    return (
      <span ref={textElementRef} className={textClasses} style={textStyle}>
        {characterTruncatedContent ?? children}
      </span>
    );
  };

  const isTooltipDisabled =
    !shouldShowTooltip ||
    (expandButton !== undefined && truncateWithButtonResult.isExpanded);

  return (
    <Tooltip
      {...tooltipProps?.root}
      type="simple"
      isDisabled={isTooltipDisabled}
      isOpen={isTooltipOpen}
      onOpenChange={(open) => {
        if (open) {
          setIsTooltipOpen(checkTruncationForTooltip());
        } else {
          setIsTooltipOpen(false);
        }
      }}
    >
      <Tooltip.Trigger {...tooltipProps?.trigger} hasButtonWrapper>
        <Typography
          as={as ?? 'div'}
          ref={ref}
          variant={typographyVariant}
          className={classNames('ndl-text-overflow', className)}
          htmlAttributes={{
            ...htmlAttributes,
          }}
          {...restProps}
          {...(!isTooltipDisabled &&
            expandButton === undefined && {
              tabIndex: 0,
            })}
        >
          <div className="ndl-text-overflow-content" ref={contentRef}>
            {renderContent()}
          </div>
        </Typography>
      </Tooltip.Trigger>
      <Tooltip.Content
        className={classNames(
          'ndl-text-overflow-tooltip-content',
          tooltipProps?.content?.className,
        )}
        {...tooltipProps?.content}
      >
        {textContent}
      </Tooltip.Content>
    </Tooltip>
  );
};
